import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { api } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import {
  ArrowLeftIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline'
import { PRODUCT_CATEGORIES, CATEGORY_GROUPS } from '../../constants/product-categories'

interface ProductFormData {
  name: string
  category: string
  description: string
  price: string
  compareAtPrice: string
  quantity: string
  weight: string
  width: string
  height: string
  length: string
  parcelValue: string
  sku: string
  barcode: string
  images: string[]
  attributes: {
    material: string
    color: string
    size: string
  }
  isActive: boolean
  isFeatured: boolean
}

const EditProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    category: 'PRAYER_WEAR',
    description: '',
    price: '',
    compareAtPrice: '',
    quantity: '0',
    weight: '',
    width: '',
    height: '',
    length: '',
    parcelValue: '',
    sku: '',
    barcode: '',
    images: [],
    attributes: {
      material: '',
      color: '',
      size: '',
    },
    isActive: true,
    isFeatured: false,
  })

  useEffect(() => {
    if (id) {
      fetchProduct(id)
    }
  }, [id])

  const fetchProduct = async (productId: string) => {
    try {
      setLoading(true)
      const response = await api.get(`/souvenir-products/${productId}`)
      const product = response.data

      setFormData({
        name: product.name || '',
        category: product.category || 'PRAYER_WEAR',
        description: product.description || '',
        price: typeof product.price === 'number' ? product.price.toString() : product.price,
        compareAtPrice: product.compareAtPrice
          ? (typeof product.compareAtPrice === 'number' ? product.compareAtPrice.toString() : product.compareAtPrice)
          : '',
        quantity: product.quantity?.toString() || '0',
        weight: product.weight != null ? product.weight.toString() : '',
        width: product.width != null ? product.width.toString() : '',
        height: product.height != null ? product.height.toString() : '',
        length: product.length != null ? product.length.toString() : '',
        parcelValue: product.parcelValue != null ? product.parcelValue.toString() : '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        images: product.images || [],
        attributes: {
          material: product.attributes?.material || '',
          color: product.attributes?.color || '',
          size: product.attributes?.size || '',
        },
        isActive: product.isActive ?? true,
        isFeatured: product.isFeatured ?? false,
      })
    } catch (err: any) {
      console.error('Error fetching product:', err)
      setError(err.response?.data?.message || 'Failed to fetch product')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked,
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const handleAttributeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [name]: value,
      },
    }))
  }

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadingImage, setUploadingImage] = useState(false)

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingImage(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post('/souvenir-products/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, res.data.url],
      }))
    } catch (err: any) {
      console.error('Error uploading image:', err)
    } finally {
      setUploadingImage(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleImageRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSaving(true)

    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        compareAtPrice: formData.compareAtPrice ? parseFloat(formData.compareAtPrice) : null,
        quantity: parseInt(formData.quantity) || 0,
        weight: parseFloat(formData.weight) || 0,
        width: formData.width ? parseFloat(formData.width) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        length: formData.length ? parseFloat(formData.length) : null,
        parcelValue: formData.parcelValue ? parseFloat(formData.parcelValue) : null,
        inStock: parseInt(formData.quantity) > 0,
      }

      await api.put(`/souvenir-products/${id}`, payload)
      navigate(`/vendor/products/${id}`)
    } catch (err: any) {
      console.error('Error updating product:', err)
      setError(err.response?.data?.message || 'Failed to update product')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/vendor/products/${id}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Back to Product
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Product</h1>
        <p className="text-gray-600 mt-1">Update product information</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>

          <div className="grid grid-cols-1 gap-6">
            {/* Product Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Product Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
              />
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                name="category"
                required
                value={formData.category}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
              >
                <optgroup label={CATEGORY_GROUPS[0].label}>
                  {PRODUCT_CATEGORIES
                    .filter(c => c.group === CATEGORY_GROUPS[0].value)
                    .map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                </optgroup>
                {PRODUCT_CATEGORIES
                  .filter(c => !c.group)
                  .map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                required
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
              />
            </div>
          </div>
        </div>

        {/* Pricing & Inventory */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing & Inventory</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Price */}
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                Price (RM) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="price"
                name="price"
                required
                min="0"
                step="0.01"
                value={formData.price}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
              />
            </div>

            {/* Compare At Price */}
            <div>
              <label htmlFor="compareAtPrice" className="block text-sm font-medium text-gray-700">
                Compare At Price (RM) (Optional)
              </label>
              <input
                type="number"
                id="compareAtPrice"
                name="compareAtPrice"
                min="0"
                step="0.01"
                value={formData.compareAtPrice}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
                placeholder="Original price for showing discount"
              />
            </div>

            {/* Quantity */}
            <div>
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                Quantity in Stock <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="quantity"
                name="quantity"
                required
                min="0"
                value={formData.quantity}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
              />
            </div>

            {/* SKU */}
            <div>
              <label htmlFor="sku" className="block text-sm font-medium text-gray-700">
                SKU
              </label>
              <input
                type="text"
                id="sku"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
              />
            </div>

            {/* Barcode */}
            <div>
              <label htmlFor="barcode" className="block text-sm font-medium text-gray-700">
                Barcode
              </label>
              <input
                type="text"
                id="barcode"
                name="barcode"
                value={formData.barcode}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active (visible to customers)
              </label>
            </div>

            {/* Featured Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isFeatured"
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isFeatured" className="ml-2 block text-sm text-gray-900">
                Featured (show on homepage)
              </label>
            </div>
          </div>
        </div>

        {/* Shipping & Dimensions */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Shipping &amp; Dimensions</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Weight */}
            <div>
              <label htmlFor="weight" className="block text-sm font-medium text-gray-700">
                Weight (KG) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="weight"
                name="weight"
                required
                min="0"
                step="0.01"
                value={formData.weight}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
                placeholder="0.00"
              />
            </div>

            {/* Width */}
            <div>
              <label htmlFor="width" className="block text-sm font-medium text-gray-700">
                Width (CM)
              </label>
              <input
                type="number"
                id="width"
                name="width"
                min="0"
                step="0.01"
                value={formData.width}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
                placeholder="0.00"
              />
            </div>

            {/* Height */}
            <div>
              <label htmlFor="height" className="block text-sm font-medium text-gray-700">
                Height (CM)
              </label>
              <input
                type="number"
                id="height"
                name="height"
                min="0"
                step="0.01"
                value={formData.height}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
                placeholder="0.00"
              />
            </div>

            {/* Length */}
            <div>
              <label htmlFor="length" className="block text-sm font-medium text-gray-700">
                Length (CM)
              </label>
              <input
                type="number"
                id="length"
                name="length"
                min="0"
                step="0.01"
                value={formData.length}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
                placeholder="0.00"
              />
            </div>

            {/* Parcel Value */}
            <div>
              <label htmlFor="parcelValue" className="block text-sm font-medium text-gray-700">
                Parcel Value (RM)
              </label>
              <input
                type="number"
                id="parcelValue"
                name="parcelValue"
                min="0"
                step="0.01"
                value={formData.parcelValue}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        {/* Attributes */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Product Attributes</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Material */}
            <div>
              <label htmlFor="material" className="block text-sm font-medium text-gray-700">
                Material
              </label>
              <input
                type="text"
                id="material"
                name="material"
                value={formData.attributes.material}
                onChange={handleAttributeChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
              />
            </div>

            {/* Color */}
            <div>
              <label htmlFor="color" className="block text-sm font-medium text-gray-700">
                Color
              </label>
              <input
                type="text"
                id="color"
                name="color"
                value={formData.attributes.color}
                onChange={handleAttributeChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
              />
            </div>

            {/* Size */}
            <div>
              <label htmlFor="size" className="block text-sm font-medium text-gray-700">
                Size
              </label>
              <input
                type="text"
                id="size"
                name="size"
                value={formData.attributes.size}
                onChange={handleAttributeChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
              />
            </div>
          </div>
        </div>

        {/* Images */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Product Images</h3>

          <div className="flex items-center justify-center w-full">
            <div className="w-full">
              {formData.images.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {formData.images.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Product ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleImageRemove(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                  <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">No images added yet</p>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleImageUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 text-sm font-medium text-gray-700 hover:border-primary-500 hover:text-primary-500 transition disabled:opacity-50"
              >
                {uploadingImage ? 'Uploading...' : 'Upload Image'}
              </button>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex items-center justify-end gap-4 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate(`/vendor/products/${id}`)}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default EditProductPage
